/**
 * Mini TV Football execution: two-video swap, score overlay, goal notification, replay badge, overlays.
 * Same structure and styles as platform-tv FootballExecutionScreen; relative sizing for mini viewport.
 */
import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import Emitter from 'shared/events/emitter';
import EVENT_NAMES from 'shared/constants/events';
import { getFootball3dTeamLogoUrl } from 'shared/utils/cdn';
import OverlayContainer from 'features/video/components/overlays/OverlayContainer';
import MiniTvFootball3dSidebarScores from 'features/video/components/mini-tv/MiniTvFootball3dSidebarScores';

import { useHlsOrSrc } from 'features/video/components/mini-tv/useHlsOrSrc';
import { getFootball3dTeamThemeCssVars } from 'features/video/components/mini-tv/football3dTeamThemes';
import { useSegmentPreloader } from 'features/video/hooks/useSegmentPreloader';
import MiniTvUiOverlayContext from './MiniTvUiOverlayContext';
import { DEFAULT_FOOTBALL_SCORE } from './miniTvDefaults';
import { withBasePath } from 'shared/utils/runtimeBasePath';

// Transition durations (ms) per sequence length – mirrored from platform-tv useFootballVideo.
const TRANSITION_DURATIONS = {
  1: 5000,
  2: 4800,
  3: 4500,
  4: 4200,
  5: 4000,
  6: 3800,
  7: 3600,
  8: 3400,
  9: 3200,
  10: 3000,
};
const TRANSITION_ENTER_MS = 900;
const JOIN_LATE_LOADER_TIMEOUT_MS = 10000;
const TRANSITION_MAX_WAIT_MS = 15000;


function initialScoreAndOrders(eventResults, upToIndex) {
  const actionTypeSequence = Array.isArray(eventResults?.video_action_type_sequence) ? eventResults.video_action_type_sequence : [];
  const scoringTeamSequence = Array.isArray(eventResults?.video_scoring_team_sequence) ? eventResults.video_scoring_team_sequence : [];
  const actionOrderSequence = Array.isArray(eventResults?.video_action_order_sequence) ? eventResults.video_action_order_sequence : [];
  const replaySequence = Array.isArray(eventResults?.video_is_replay_sequence) ? eventResults.video_is_replay_sequence : [];
  let home = 0;
  let away = 0;
  const orders = new Set();
  for (let i = 0; i < upToIndex && i < actionTypeSequence.length; i++) {
    const isReplay = replaySequence[i] === true;
    if (actionTypeSequence[i] === 'GOL' && !isReplay) {
      const order = actionOrderSequence[i];
      if (order != null) orders.add(order);
      const team = scoringTeamSequence[i];
      if (team === 'HOME') home += 1;
      else if (team === 'AWAY') away += 1;
    }
  }
  return { home, away, orders };
}

/**
 * Build execution timeline from eventResults: segment meta and cumulative score per segment.
 * Single source of truth for join-late initial score and playback score updates.
 * If videoUrlCount is provided and larger than metadata length, extends timeline so scoreAtSegment has an entry per video.
 */
function buildFootballExecutionTimeline(eventResults, videoUrlCount) {
  const actionTypeSequence = Array.isArray(eventResults?.video_action_type_sequence) ? eventResults.video_action_type_sequence : [];
  const scoringTeamSequence = Array.isArray(eventResults?.video_scoring_team_sequence) ? eventResults.video_scoring_team_sequence : [];
  const actionOrderSequence = Array.isArray(eventResults?.video_action_order_sequence) ? eventResults.video_action_order_sequence : [];
  const replaySequence = Array.isArray(eventResults?.video_is_replay_sequence) ? eventResults.video_is_replay_sequence : [];
  const segmentMeta = [];
  const scoreAtSegment = [];
  let home = 0;
  let away = 0;
  const n = Math.max(
    actionTypeSequence.length,
    scoringTeamSequence.length,
    actionOrderSequence.length,
    replaySequence.length,
    videoUrlCount ?? 0
  );
  for (let i = 0; i < n; i++) {
    const actionType = actionTypeSequence[i] != null ? String(actionTypeSequence[i]) : null;
    const isReplay = replaySequence[i] === true;
    const isScoreGoal = actionType === 'GOL' && !isReplay;
    const order = actionOrderSequence[i] != null && Number.isFinite(Number(actionOrderSequence[i])) ? Number(actionOrderSequence[i]) : null;
    const scoringTeam = scoringTeamSequence[i] != null ? String(scoringTeamSequence[i]) : null;
    segmentMeta.push({
      segmentIndex: i,
      isReplay,
      actionType,
      order,
      scoringTeam,
      isScoreGoal,
    });
    if (isScoreGoal) {
      if (scoringTeam === 'HOME') home += 1;
      else if (scoringTeam === 'AWAY') away += 1;
    }
    scoreAtSegment.push({ home, away });
  }
  return { segmentMeta, scoreAtSegment };
}

/** 1-based action index (1..8) for the segment we're about to play; replays don't advance the bar. */
function actionIndexForSegment(segmentIndex, replaySequence) {
  if (!Array.isArray(replaySequence)) return Math.min(8, segmentIndex + 1);
  let count = 0;
  for (let i = 0; i < segmentIndex; i++) {
    if (replaySequence[i] !== true) count += 1;
  }
  return Math.min(8, count + 1);
}

const SIDEBAR_WIDTH = 252;

export default function MiniTvFootballExecution({
  eventId,
  event,
  eventResults,
  videoUrls,
  avh,
  onVideoReady,
  onExecutionEnded,
  joinElapsedSeconds = 0,
  initialSegmentIndex,
  initialSegmentSeekSeconds,
  otherWeekEvents,
  weekResults,
  muted = true,
}) {
  const { t } = useTranslation();
  const uiOverlayRef = React.useContext(MiniTvUiOverlayContext);
  const [uiOverlayTarget, setUiOverlayTarget] = React.useState(null);
  React.useEffect(() => {
    if (uiOverlayRef?.current && !uiOverlayTarget) setUiOverlayTarget(uiOverlayRef.current);
  }, [uiOverlayRef?.current, uiOverlayTarget]);
  const mainRef = React.useRef(null);
  const nextRef = React.useRef(null);
  const transitionTimeoutRef = React.useRef(null);
  const initialTransitionDoneRef = React.useRef(false);
  const transitionVisibleRef = React.useRef(false);
  const startIndex =
    initialSegmentIndex != null && videoUrls?.length
      ? Math.min(Math.max(0, Number(initialSegmentIndex)), videoUrls.length - 1)
      : 0;
  const timeline = React.useMemo(
    () => buildFootballExecutionTimeline(eventResults, videoUrls?.length),
    [eventResults, videoUrls?.length]
  );
  const initialScoreFromTimeline = React.useMemo(() => {
    if (!timeline) return { ...DEFAULT_FOOTBALL_SCORE };
    if (startIndex <= 0) return { ...DEFAULT_FOOTBALL_SCORE };
    const idx = Math.min(startIndex - 1, timeline.scoreAtSegment.length - 1);
    const prev = idx >= 0 ? timeline.scoreAtSegment[idx] : null;
    return prev ?? { ...DEFAULT_FOOTBALL_SCORE };
  }, [timeline, startIndex]);
  const initialOrdersFromTimeline = React.useMemo(() => {
    if (!timeline) return new Set();
    const sliceEnd = Math.min(startIndex, timeline.segmentMeta.length);
    return new Set(
      timeline.segmentMeta.slice(0, sliceEnd).filter((m) => m.isScoreGoal && m.order != null).map((m) => m.order)
    );
  }, [timeline, startIndex]);
  const [currentIndex, setCurrentIndex] = React.useState(startIndex);
  const [liveScore, setLiveScore] = React.useState(initialScoreFromTimeline);
  const initialSeekAppliedRef = React.useRef(false);
  const isJoinLate = initialSegmentIndex > 0 || (initialSegmentSeekSeconds != null && Number(initialSegmentSeekSeconds) > 0);
  const [joinLateReady, setJoinLateReady] = React.useState(() => !isJoinLate);
  const [goalVisible, setGoalVisible] = React.useState(false);
  const appliedOrdersRef = React.useRef(initialOrdersFromTimeline);
  const goalDispatchedForIndexRef = React.useRef(new Set());
  const lastGoalOrderRef = React.useRef(null);
  const goalTimeoutRef = React.useRef(null);
  const goalScheduledRef = React.useRef({ index: null, order: null });
  const firstPlaybackDispatchedRef = React.useRef(false);
  const [executionTimeline, setExecutionTimeline] = React.useState(null);
  const [transitionNextActionIndex, setTransitionNextActionIndex] = React.useState(null);

  // Pause videos and clear transition timeouts when tab is hidden.
  // Parent orchestrator handles clean-slate recovery on tab return.
  React.useEffect(() => {
    const onVisChange = () => {
      if (document.visibilityState !== 'visible') {
        console.warn('[MiniTvFootball] Tab hidden — pausing execution videos');
        try { mainRef.current?.pause(); } catch { /* ignore */ }
        try { nextRef.current?.pause(); } catch { /* ignore */ }
        if (transitionTimeoutRef.current != null) {
          clearTimeout(transitionTimeoutRef.current);
          transitionTimeoutRef.current = null;
        }
        if (goalTimeoutRef.current != null) {
          clearTimeout(goalTimeoutRef.current);
          goalTimeoutRef.current = null;
        }
      }
    };
    document.addEventListener('visibilitychange', onVisChange);
    return () => document.removeEventListener('visibilitychange', onVisChange);
  }, []);

  const videoIdSequence = Array.isArray(eventResults?.video_id_sequence) ? eventResults.video_id_sequence : [];
  const minuteSequence = Array.isArray(eventResults?.video_minute_sequence) ? eventResults.video_minute_sequence : [];
  const replaySequence = Array.isArray(eventResults?.video_is_replay_sequence) ? eventResults.video_is_replay_sequence : [];
  const actionTeamCodeSequence = Array.isArray(eventResults?.video_action_team_code_sequence) ? eventResults.video_action_team_code_sequence : [];
  const actionTypeSequence = Array.isArray(eventResults?.video_action_type_sequence) ? eventResults.video_action_type_sequence : [];
  const scoringTeamSequence = Array.isArray(eventResults?.video_scoring_team_sequence) ? eventResults.video_scoring_team_sequence : [];
  const actionOrderSequence = Array.isArray(eventResults?.video_action_order_sequence) ? eventResults.video_action_order_sequence : [];

  React.useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        window.clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }
      if (goalTimeoutRef.current) {
        window.clearTimeout(goalTimeoutRef.current);
        goalTimeoutRef.current = null;
      }
      const vids = [mainRef.current, nextRef.current].filter(Boolean);
      vids.forEach((v) => {
        try {
          v.pause?.();
          v.removeAttribute?.('src');
          v.load?.();
        } catch {
          // ignore
        }
      });
    };
  }, []);

  const homeTeamCode = event?.home_team_code ?? eventResults?.home_team_code ?? event?.home_team?.code ?? eventResults?.home_team?.code ?? '';
  const awayTeamCode = event?.away_team_code ?? eventResults?.away_team_code ?? event?.away_team?.code ?? eventResults?.away_team?.code ?? '';

  const currentVideoId = videoIdSequence[currentIndex] ?? null;
  const currentUrl = videoUrls?.[currentIndex];
  const nextUrl = videoUrls?.[currentIndex + 1];
  const currentMinute = minuteSequence[currentIndex];
  const isMain = currentIndex % 2 === 0;
  const [clockMinute, setClockMinute] = React.useState(() => {
    const m = currentMinute != null ? Number(currentMinute) : 0;
    return Number.isFinite(m) ? Math.max(0, Math.floor(m)) : 0;
  });
  const lastClockMinuteRef = React.useRef(null);

  const transitionHoldMs = React.useMemo(() => {
    const n = videoUrls?.length ?? 0;
    return Math.max(3000, TRANSITION_DURATIONS[n] ?? 3000);
  }, [videoUrls?.length]);

  const preloader = useSegmentPreloader(videoUrls, avh, currentIndex);

  const totalExecutionDurationMs = React.useMemo(() => {
    const durationSec = eventResults?.duration != null ? Number(eventResults.duration) : null;
    const videoMs = durationSec != null && Number.isFinite(durationSec) && durationSec > 0 ? Math.round(durationSec * 1000) : null;
    if (!videoMs) return null;
    const nonReplayCount = Array.isArray(replaySequence) ? replaySequence.filter((x) => x !== true).length : (videoUrls?.length ?? 0);
    const transitionMs = Math.max(0, nonReplayCount) * (TRANSITION_ENTER_MS + transitionHoldMs);
    return videoMs + transitionMs;
  }, [eventResults?.duration, replaySequence, transitionHoldMs, videoUrls?.length]);

  const setTransitionVisible = React.useCallback((visible) => {
    transitionVisibleRef.current = !!visible;
    const el = typeof document !== 'undefined' ? document.getElementById('c7-mini-tv-football-transition') : null;
    const stage = typeof document !== 'undefined' ? document.getElementById('c7-mini-tv-football-video-stage') : null;
    if (el) {
      el.classList.toggle('c7-mini-tv-football-transition--visible', !!visible);
    }
    if (stage) {
      stage.classList.toggle('c7-mini-tv-football-video-stage--pushed', !!visible);
    }
  }, []);

  const setTransitionContentForIndex = React.useCallback((idx) => {
    if (typeof document === 'undefined') return;
    const teamCodeRaw = actionTeamCodeSequence?.[idx] ?? null;
    const teamCode = teamCodeRaw != null ? String(teamCodeRaw) : '';
    const transitionEl = document.getElementById('c7-mini-tv-football-transition');
    const logo = document.getElementById('c7-mini-tv-football-transition-logo');
    const team = document.getElementById('c7-mini-tv-football-transition-team');

    if (transitionEl) {
      const vars = getFootball3dTeamThemeCssVars(teamCode);
      for (const [k, v] of Object.entries(vars)) {
        transitionEl.style.setProperty(k, v);
      }
      try {
        transitionEl.dataset.teamCode = String(teamCode || '');
      } catch {
        // ignore
      }
    }
    if (logo && logo.tagName === 'IMG') {
      logo.src = getFootball3dTeamLogoUrl(teamCode || '');
    }
    if (team) {
      team.textContent = teamCode ? String(teamCode) : '';
    }

    const minuteRaw = minuteSequence?.[idx] ?? null;
    const m = minuteRaw != null ? Number(minuteRaw) : 0;
    setClockMinute(Number.isFinite(m) ? Math.max(0, Math.floor(m)) : 0);
  }, [actionTeamCodeSequence, minuteSequence]);

  const setReplayVisible = React.useCallback((visible) => {
    if (typeof document === 'undefined') return;
    const els = document.querySelectorAll('.c7-mini-tv-football-replay-overlay');
    els.forEach((el) => el.classList.toggle('c7-mini-tv-football-replay-overlay--visible', !!visible));
  }, []);

  useHlsOrSrc(isMain ? mainRef : nextRef, currentUrl, avh);
  useHlsOrSrc(isMain ? nextRef : mainRef, nextUrl, avh);

  React.useEffect(() => {
    if (initialSegmentIndex == null || !videoUrls?.length || !timeline) return;
    const idx = Math.min(Math.max(0, Number(initialSegmentIndex)), videoUrls.length - 1);
    setJoinLateReady(!(idx > 0 || (initialSegmentSeekSeconds != null && Number(initialSegmentSeekSeconds) > 0)));
    setCurrentIndex((prev) => {
      if (prev !== 0 || idx <= 0) return prev;
      return idx;
    });
    if (idx > 0) {
      const score = timeline.scoreAtSegment[Math.min(idx - 1, timeline.scoreAtSegment.length - 1)] ?? { ...DEFAULT_FOOTBALL_SCORE };
      setLiveScore(score);
      appliedOrdersRef.current = new Set(
        timeline.segmentMeta.slice(0, Math.min(idx, timeline.segmentMeta.length)).filter((m) => m.isScoreGoal && m.order != null).map((m) => m.order)
      );
    }
  }, [initialSegmentIndex, videoUrls?.length, timeline, initialSegmentSeekSeconds]);

  React.useEffect(() => {
    if (initialSegmentIndex != null && initialSegmentIndex >= 0) return;
    appliedOrdersRef.current = new Set();
    goalDispatchedForIndexRef.current = new Set();
    lastGoalOrderRef.current = null;
    setLiveScore({ ...DEFAULT_FOOTBALL_SCORE });
    initialTransitionDoneRef.current = false;
    lastClockMinuteRef.current = null;
    firstPlaybackDispatchedRef.current = false;
    setReplayVisible(false);
    setExecutionTimeline(null);
    setTransitionNextActionIndex(null);
    setCurrentIndex(0);
  }, [eventResults?.event_id, initialSegmentIndex, setReplayVisible]);

  // Anchor execution timelines when FIRST clip actually starts playing.
  React.useEffect(() => {
    const video = (isMain ? mainRef : nextRef)?.current;
    if (!video) return;
    const onPlaying = () => {
      if (firstPlaybackDispatchedRef.current) return;
      firstPlaybackDispatchedRef.current = true;
      const nowMs = Date.now();
      const joinElapsedSec = joinElapsedSeconds != null ? Number(joinElapsedSeconds) : 0;
      const durationSec = eventResults?.duration != null ? Number(eventResults.duration) : null;
      const ratio =
        joinElapsedSec > 0 && durationSec != null && Number.isFinite(durationSec) && durationSec > 0
          ? Math.max(0, Math.min(1, joinElapsedSec / durationSec))
          : 0;
      const startedAtMs =
        ratio > 0 && totalExecutionDurationMs != null && Number.isFinite(totalExecutionDurationMs) && totalExecutionDurationMs > 0
          ? nowMs - Math.round(ratio * totalExecutionDurationMs)
          : nowMs;
      const cycle = eventResults?.event_id != null ? String(eventResults.event_id) : (eventId != null ? String(eventId) : '');
      setExecutionTimeline({
        startedAtMs,
        totalDurationMs: totalExecutionDurationMs,
        eventCycleKey: cycle,
      });
    };
    video.addEventListener('playing', onPlaying);
    return () => video.removeEventListener('playing', onPlaying);
  }, [isMain, currentIndex, totalExecutionDurationMs, eventResults?.event_id, eventResults?.duration, eventId, joinElapsedSeconds]);

  // Minute-by-minute clock interpolation (platform-tv behavior).
  React.useEffect(() => {
    const video = (isMain ? mainRef : nextRef)?.current;
    if (!video) return;

    const updateClockForPlayback = () => {
      if (!video || transitionVisibleRef.current) return;
      const startMinuteRaw = minuteSequence?.[currentIndex];
      const nextMinuteRaw = minuteSequence?.[currentIndex + 1];
      const startMinute = startMinuteRaw != null ? Number(startMinuteRaw) : 0;
      const nextMinute = nextMinuteRaw != null ? Number(nextMinuteRaw) : null;
      const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
      if (!duration) return;

      // At the end of the current clip, reach the minute BEFORE the next clip starts.
      const targetMinute = nextMinute != null && Number.isFinite(Number(nextMinute))
        ? Math.max(startMinute, Number(nextMinute) - 1)
        : startMinute;

      const progress = Math.min(1, Math.max(0, video.currentTime / duration));
      const minuteFloat = startMinute + (targetMinute - startMinute) * progress;
      const minuteInt = Math.max(0, Math.floor(Number.isFinite(minuteFloat) ? minuteFloat : 0));

      if (lastClockMinuteRef.current !== minuteInt) {
        lastClockMinuteRef.current = minuteInt;
      }
    };

    video.addEventListener('timeupdate', updateClockForPlayback);
    video.addEventListener('loadedmetadata', updateClockForPlayback);
    return () => {
      video.removeEventListener('timeupdate', updateClockForPlayback);
      video.removeEventListener('loadedmetadata', updateClockForPlayback);
    };
  }, [currentIndex, isMain, minuteSequence, currentUrl]);

  // Platform-tv parity: dispatch football3d:goal ~1s before end of GOAL clip (non-replay).
  // Use timeupdate so we fire when playback actually reaches (duration - 1), not from a single duration snapshot (HLS can report wrong duration at load).
  React.useEffect(() => {
    const video = (isMain ? mainRef : nextRef)?.current;
    if (!video) return;

    const isGoalClip = actionTypeSequence?.[currentIndex] === 'GOL';
    const isReplayClip = replaySequence?.[currentIndex] === true;
    const order = actionOrderSequence?.[currentIndex];
    const scoringTeam = scoringTeamSequence?.[currentIndex];

    const clearScheduled = () => {
      goalScheduledRef.current = { index: null, order: null };
      if (goalTimeoutRef.current) {
        window.clearTimeout(goalTimeoutRef.current);
        goalTimeoutRef.current = null;
      }
    };

    clearScheduled();
    if (!isGoalClip || isReplayClip) return;
    if (order == null || appliedOrdersRef.current.has(order)) return;

    const tryFireGoal = () => {
      if (!video || appliedOrdersRef.current.has(order)) return;
      const d = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null;
      if (!d || d < 2) return; // require sane duration (clip is at least 2s)
      const remaining = d - video.currentTime;
      if (remaining > 1) return; // fire when <= 1s left
      // Within 1 second of end – fire once
      goalScheduledRef.current = { index: null, order: null };
      if (goalTimeoutRef.current) {
        window.clearTimeout(goalTimeoutRef.current);
        goalTimeoutRef.current = null;
      }
      if (appliedOrdersRef.current.has(order)) return;
      window.dispatchEvent(
        new CustomEvent('football3d:goal', {
          detail: { index: currentIndex, order, scoringTeam },
        })
      );
    };

    const onTimeUpdate = () => tryFireGoal();
    const onLoadedMetadata = () => tryFireGoal();

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    tryFireGoal();
    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      clearScheduled();
    };
  }, [currentIndex, isMain, actionTypeSequence, actionOrderSequence, scoringTeamSequence, replaySequence]);

  React.useEffect(() => {
    const onGoal = (ev) => {
      const detail = ev?.detail ?? {};
      const index = detail?.index != null ? Number(detail.index) : null;
      if (index != null && goalDispatchedForIndexRef.current.has(index)) return;
      const order = detail?.order != null ? Number(detail.order) : (detail?.index != null ? Number(detail.index) + 1 : null);
      const dedupeKey = order;
      if (dedupeKey == null || appliedOrdersRef.current.has(dedupeKey)) return;
      if (index != null) goalDispatchedForIndexRef.current.add(index);
      appliedOrdersRef.current.add(dedupeKey);

      setLiveScore((prev) => {
        const st = detail?.scoringTeam ? String(detail.scoringTeam) : null;
        if (st === 'HOME') return { ...prev, home: prev.home + 1 };
        if (st === 'AWAY') return { ...prev, away: prev.away + 1 };
        return prev;
      });

      if (lastGoalOrderRef.current !== dedupeKey) {
        lastGoalOrderRef.current = dedupeKey;
        setGoalVisible(true);
        window.setTimeout(() => setGoalVisible(false), 1400);
      }
      const el = typeof document !== 'undefined' ? document.getElementById('c7-mini-tv-football-goal-notification') : null;
      if (el) {
        el.classList.remove('hidden');
        el.classList.add('c7-mini-tv-football-goal-notification--visible');
        window.setTimeout(() => {
          el.classList.remove('c7-mini-tv-football-goal-notification--visible');
          el.classList.add('hidden');
        }, 1400);
      }
    };
    window.addEventListener('football3d:goal', onGoal);
    return () => window.removeEventListener('football3d:goal', onGoal);
  }, []);

  React.useEffect(() => {
    const video = (isMain ? mainRef : nextRef)?.current;
    if (!video || !currentUrl) return;
    const seekSec = initialSegmentSeekSeconds != null && Number(initialSegmentSeekSeconds) > 0 ? Number(initialSegmentSeekSeconds) : null;
    const onCanPlay = () => {
      console.log('[MiniTvFootball] canplay fired', {
        readyState: video.readyState,
        paused: video.paused,
        currentTime: video.currentTime,
        segmentIndex: currentIndex,
      });
      if (seekSec != null && currentIndex === startIndex && !initialSeekAppliedRef.current) {
        initialSeekAppliedRef.current = true;
        const d = video.duration;
        const target = Number.isFinite(d) && d > 0 ? Math.min(seekSec, d) : seekSec;
        video.currentTime = target;
        const onSeeked = () => {
          video.removeEventListener('seeked', onSeeked);
          setJoinLateReady(true);
        };
        video.addEventListener('seeked', onSeeked);
      } else {
        setJoinLateReady(true);
      }
      onVideoReady?.();

      // Platform-tv behavior: show transition before first non-replay clip.
      const isFirstReplay = replaySequence?.[0] === true;
      const needsInitialTransition = currentIndex === 0 && !initialTransitionDoneRef.current && !isFirstReplay;
      if (needsInitialTransition) {
        initialTransitionDoneRef.current = true;
        setReplayVisible(false);
        setTransitionNextActionIndex(1);
        setTransitionContentForIndex(0);
        setTransitionVisible(true);
        if (transitionTimeoutRef.current) window.clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = window.setTimeout(() => {
          transitionTimeoutRef.current = null;
          setTransitionNextActionIndex(null);
          setTransitionVisible(false);
          video.play().catch((err) => console.warn('[MiniTvFootball] play() rejected:', err?.message || err));
        }, TRANSITION_ENTER_MS + transitionHoldMs);
        return;
      }

      // First clip is a replay: show replay badge immediately.
      if (currentIndex === 0 && isFirstReplay && !initialTransitionDoneRef.current) {
        initialTransitionDoneRef.current = true;
        setReplayVisible(true);
      }

      if (!transitionVisibleRef.current) {
        video.play().catch((err) => console.warn('[MiniTvFootball] play() rejected:', err?.message || err));
      }
    };
    video.addEventListener('canplay', onCanPlay);
    if (video.readyState >= 2) onCanPlay();
    return () => video.removeEventListener('canplay', onCanPlay);
  }, [currentIndex, currentUrl, isMain, onVideoReady, initialSegmentSeekSeconds, startIndex, replaySequence, setTransitionContentForIndex, setTransitionVisible, setReplayVisible, transitionHoldMs]);

  React.useEffect(() => {
    if (!isJoinLate) return;
    const t = window.setTimeout(() => setJoinLateReady(true), JOIN_LATE_LOADER_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [isJoinLate]);

  React.useEffect(() => {
    const video = (isMain ? mainRef : nextRef)?.current;
    if (!video || !isJoinLate) return;
    const onError = () => setJoinLateReady(true);
    video.addEventListener('error', onError);
    return () => video.removeEventListener('error', onError);
  }, [currentIndex, isMain, isJoinLate]);

  React.useEffect(() => {
    const main = mainRef?.current;
    const next = nextRef?.current;
    if (!main || !currentUrl) return;
    const isLastVideo = currentIndex >= (videoUrls?.length ?? 1) - 1;
    const video = isMain ? main : next;
    const handleEnded = () => {
      if (isLastVideo) {
        onExecutionEnded?.();
        return;
      }
      const nextIndex = currentIndex + 1;
      const nextIsReplay = replaySequence[nextIndex] === true;
      if (nextIsReplay) {
        setTransitionVisible(false);
        setReplayVisible(true);
        setCurrentIndex(nextIndex);
        return;
      }

      setReplayVisible(false);
      setTransitionContentForIndex(nextIndex);
      setTransitionNextActionIndex(actionIndexForSegment(nextIndex, replaySequence));
      setTransitionVisible(true);
      if (transitionTimeoutRef.current) window.clearTimeout(transitionTimeoutRef.current);

      const nextVideoEl = (isMain ? nextRef : mainRef)?.current;
      const dismissTransition = () => {
        transitionTimeoutRef.current = null;
        setTransitionNextActionIndex(null);
        setReplayVisible(false);
        setCurrentIndex(nextIndex);
        setTransitionVisible(false);
      };
      let minElapsed = false;
      let videoReady = nextVideoEl ? nextVideoEl.readyState >= 2 : true;
      let dismissed = false;
      const tryDismiss = () => {
        if (dismissed || !minElapsed || !videoReady) return;
        dismissed = true;
        dismissTransition();
      };
      transitionTimeoutRef.current = window.setTimeout(() => { minElapsed = true; tryDismiss(); }, TRANSITION_ENTER_MS + transitionHoldMs);
      const maxTimer = window.setTimeout(() => { if (!dismissed) { dismissed = true; dismissTransition(); } }, TRANSITION_MAX_WAIT_MS);
      if (!videoReady && nextVideoEl) {
        const onCanPlay = () => { nextVideoEl.removeEventListener('canplay', onCanPlay); videoReady = true; tryDismiss(); };
        nextVideoEl.addEventListener('canplay', onCanPlay);
      } else {
        tryDismiss();
      }
    };
    video.addEventListener('ended', handleEnded);
    return () => video.removeEventListener('ended', handleEnded);
  }, [currentIndex, videoUrls?.length, isMain, onExecutionEnded, replaySequence, setTransitionContentForIndex, setTransitionVisible, setReplayVisible, transitionHoldMs, currentUrl]);

  React.useEffect(() => {
    const video = (isMain ? mainRef : nextRef)?.current;
    if (!video || !currentVideoId) return;
    const emitTime = () => Emitter.emit(EVENT_NAMES.MEDIA.TIMEUPDATE_PROCEED + '-' + currentVideoId, { time: video.currentTime });
    video.addEventListener('timeupdate', emitTime);
    return () => video.removeEventListener('timeupdate', emitTime);
  }, [currentIndex, currentVideoId, isMain]);

  if (!videoUrls?.length || !homeTeamCode || !awayTeamCode) return null;

  const clockText = `${Math.max(0, Math.floor(Number.isFinite(clockMinute) ? clockMinute : 0))}'`;
  const otherEvents = Array.isArray(otherWeekEvents) ? otherWeekEvents.slice(0, 9) : [];
  const sidebarResults = weekResults && typeof weekResults === 'object' ? weekResults : {};

  return (
    <div
      className="c7-mini-tv-execution c7-mini-tv-football-execution"
      style={{ ['--c7-mini-tv-football-sidebar-width']: `${SIDEBAR_WIDTH}px` }}
    >
      <div className="c7-mini-tv-football-stage">
        <div className="c7-mini-tv-football-video-stage" id="c7-mini-tv-football-video-stage">
          <div className="c7-mini-tv-football-video-wrap">
            <video
              ref={mainRef}
              className={`c7-mini-tv-football-video ${isMain ? '' : 'c7-mini-tv-football-video--hidden'}`}
              id="c7-mini-tv-football-video-main"
              playsInline
              muted={muted}
            />
            <video
              ref={nextRef}
              className={`c7-mini-tv-football-video ${!isMain ? '' : 'c7-mini-tv-football-video--hidden'}`}
              id="c7-mini-tv-football-video-next"
              playsInline
              muted={muted}
            />
          </div>
          {currentVideoId && (
            <div className="c7-mini-tv-overlay-wrapper">
              <OverlayContainer video_id={currentVideoId} scale_factor={1} />
            </div>
          )}
          {!uiOverlayTarget && (
            <div className="c7-mini-tv-football-replay-overlay" aria-label="Replay">
              REPLAY
            </div>
          )}
          {!joinLateReady && isJoinLate && (
            <div className="c7-mini-tv-join-late-loader" aria-hidden>
              <div className="c7-mini-tv-join-late-loader__spinner" />
            </div>
          )}
        </div>

        {/* Transition overlay (platform-tv parity): appears before each NON-replay clip */}
        <div
          id="c7-mini-tv-football-transition"
          className="c7-mini-tv-football-transition"
          aria-label="Action transition"
        >
          <div className="c7-mini-tv-football-transition-inner">
            <div className="c7-mini-tv-football-transition-brand">
              <img id="c7-mini-tv-football-transition-logo" className="c7-mini-tv-football-transition-logo" alt="team" />
              <div className="c7-mini-tv-football-transition-teamwrap">
                <div id="c7-mini-tv-football-transition-team" className="c7-mini-tv-football-transition-team" />
                <div className="c7-mini-tv-football-transition-action">
                  {t('ATTACK') || 'ATTACK'}
                </div>
              </div>
            </div>
            {transitionNextActionIndex != null && (
              <div className="c7-mini-tv-football-transition-actions-bar" aria-label="Actions">
                {Array.from({ length: 8 }, (_, i) => {
                  const n = i + 1;
                  const isPast = n < transitionNextActionIndex;
                  const isCurrent = n === transitionNextActionIndex;
                  const isActive = isPast || isCurrent;
                  const shouldFlash = isCurrent;
                  return (
                    <React.Fragment key={n}>
                      <span className="c7-mini-tv-football-transition-action-num">{n}</span>
                        <div
                          className={`c7-mini-tv-football-transition-action-cell ${isActive ? 'c7-mini-tv-football-transition-action-cell--active' : ''} ${shouldFlash ? 'c7-mini-tv-football-transition-action-cell--flashing' : ''}`}
                          style={{ backgroundImage: `url(${withBasePath('/images/football/sport-icon.svg')})` }}
                        >
                        </div>
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <MiniTvFootball3dSidebarScores otherEvents={otherEvents} weekResults={sidebarResults} executionTimeline={executionTimeline} />

      {uiOverlayTarget && ReactDOM.createPortal(
        <>
          <div className="c7-mini-tv-football-score-overlay">
            <div className="c7-mini-tv-football-score-container">
              {eventId && (
                <div className="c7-mini-tv-football-event-id-chip" aria-label="Event ID">
                  <span className="c7-mini-tv-football-event-id-chip__label">ID</span>
                  <span>#</span>
                  <span>{eventId}</span>
                </div>
              )}
              <div className="c7-mini-tv-football-team-section c7-mini-tv-football-team-section--home">
                <img
                  className="c7-mini-tv-football-team-logo"
                  src={getFootball3dTeamLogoUrl(homeTeamCode)}
                  alt={homeTeamCode}
                  onError={(e) => { e.target.src = getFootball3dTeamLogoUrl(''); }}
                />
                <span className="c7-mini-tv-football-team-code">{homeTeamCode}</span>
              </div>
              <div className="c7-mini-tv-football-score-section">
                <span className="c7-mini-tv-football-score c7-mini-tv-football-score--home">{liveScore.home}</span>
                <span className="c7-mini-tv-football-score-sep">-</span>
                <span className="c7-mini-tv-football-score c7-mini-tv-football-score--away">{liveScore.away}</span>
              </div>
              <div className="c7-mini-tv-football-team-section c7-mini-tv-football-team-section--away">
                <span className="c7-mini-tv-football-team-code">{awayTeamCode}</span>
                <img
                  className="c7-mini-tv-football-team-logo"
                  src={getFootball3dTeamLogoUrl(awayTeamCode)}
                  alt={awayTeamCode}
                  onError={(e) => { e.target.src = getFootball3dTeamLogoUrl(''); }}
                />
              </div>
              <div className="c7-mini-tv-football-clock-badge" aria-label="Match clock">
                <svg className="c7-mini-tv-football-clock-icon" viewBox="0 0 24 24" aria-hidden>
                  <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 7v5l3 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div className="c7-mini-tv-football-clock-text">{clockText}</div>
              </div>
            </div>
          </div>

          <div className="c7-mini-tv-football-replay-overlay" aria-label="Replay">
            REPLAY
          </div>

          <div
            id="c7-mini-tv-football-goal-notification"
            className={`c7-mini-tv-football-goal-notification ${goalVisible ? '' : 'hidden'}`}
          >
            <div className="c7-mini-tv-football-goal-text">{t('GOAL') || 'GOAL!'}</div>
          </div>
        </>,
        uiOverlayTarget
      )}
    </div>
  );
}
